/**
 * PRISMA 2020 diagram — TipTap block node.
 *
 * The node stores a frozen snapshot of the PRISMA statistics at insertion time
 * plus an optional caption. A "Régénérer" button fetches fresh data from the
 * project's /prisma/snapshot endpoint and updates the attrs.
 *
 * Attrs
 * ─────
 * stats        – JSON snapshot (PrismaSnapshot)
 * generatedAt  – ISO date string
 * caption      – user-editable legend text
 * projectId    – project ULID (needed to refetch)
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import PrismaNodeView from './PrismaNodeView.vue';

export interface PrismaIdentification {
    from_databases: number;
    from_other_sources: number;
    total: number;
}

export interface PrismaSnapshot {
    identification: PrismaIdentification;
    duplicates_removed: number;
    screening: { screened: number; excluded: number };
    eligibility: { full_text_assessed: number; excluded: number };
    included: number;
    generated_at: string;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        prisma: {
            insertPrisma: (attrs?: {
                stats?: PrismaSnapshot | null;
                generatedAt?: string | null;
                caption?: string;
                projectId?: string;
            }) => ReturnType;
        };
    }
}

export const PrismaExtension = Node.create({
    name: 'prisma',
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,

    addAttributes() {
        return {
            stats: {
                default: null,
                parseHTML: (el) => {
                    const raw = el.getAttribute('data-stats');

                    try {
                        return raw ? JSON.parse(raw) : null;
                    } catch {
                        return null;
                    }
                },
                renderHTML: (attrs) => ({
                    'data-stats': attrs.stats
                        ? JSON.stringify(attrs.stats)
                        : '',
                }),
            },
            generatedAt: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-generated-at'),
                renderHTML: (attrs) => ({
                    'data-generated-at': attrs.generatedAt ?? '',
                }),
            },
            caption: {
                default: 'Diagramme PRISMA 2020',
                parseHTML: (el) => el.getAttribute('data-caption'),
                renderHTML: (attrs) => ({
                    'data-caption': attrs.caption ?? '',
                }),
            },
            projectId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-project-id'),
                renderHTML: (attrs) => ({
                    'data-project-id': attrs.projectId ?? '',
                }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="prisma"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-type': 'prisma' }),
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(PrismaNodeView);
    },

    addCommands() {
        return {
            insertPrisma:
                (attrs = {}) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            stats: attrs.stats ?? null,
                            generatedAt: attrs.generatedAt ?? null,
                            caption: attrs.caption ?? 'Diagramme PRISMA 2020',
                            projectId: attrs.projectId ?? null,
                        },
                    });
                },
        };
    },
});
